import { cs } from '../../lib/currency';
import { useEffect } from 'react';
import { Building2, CreditCard, Loader2 } from 'lucide-react';

/**
 * Pre-checkout Card vs ACH chooser.
 *
 * Until Feb 2026 the academy added a CC surcharge as a line item to every
 * Stripe Checkout session and then auto-refunded it after the fact when
 * the customer paid by ACH. That refund codepath was fragile (Stripe SDK
 * deprecation + async settlement timing + webhook ordering), so we now
 * ask the customer upfront and lock the Stripe session to one method:
 *   - card            → 3% CC surcharge added to total
 *   - us_bank_account → no surcharge
 *
 * Behaviour:
 *   - No default selection (user must actively pick — locked in by product).
 *   - Live total preview shows both options with their respective totals
 *     so the savings are obvious.
 *   - `disabled` prop greys out the radios while the parent flow is busy.
 *
 * Props:
 *   - baseAmount         (number, required) — subtotal in dollars
 *   - surchargePercent   (number) — CC surcharge percent. Default 3.
 *   - value              ('card' | 'us_bank_account' | null)
 *   - onChange(method)
 *   - disabled?          (bool)
 *   - achAvailable?      (bool) — false on anonymous public flows; falls
 *                                 back to a single read-only "Card" tile.
 *   - savedCardLast4?    (string) — when present, render a "Pays from
 *                                   •••• 1234" chip on the Card tile.
 *   - savedCardBrand?    (string) — visa/mc/amex/etc. label.
 *   - savedAchLast4?     (string) — same idea for ACH tile.
 *   - savedAchBank?      (string) — bank name label.
 *   - className?         (string)
 */
const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

export default function PaymentMethodChooser({
  baseAmount,
  surchargePercent = 3,
  value,
  onChange,
  disabled = false,
  achAvailable = true,
  savedCardLast4 = null,
  savedCardBrand = null,
  savedAchLast4 = null,
  savedAchBank = null,
  className = '',
}) {
  const base = Number(baseAmount) || 0;
  const surcharge = Math.round(base * (surchargePercent / 100) * 100) / 100;
  const cardTotal = base + surcharge;
  const achTotal = base;

  // If ACH isn't available, force-select card (anonymous flows).
  useEffect(() => {
    if (!achAvailable && value !== 'card') {
      onChange?.('card');
    }
  }, [achAvailable, value, onChange]);

  const pick = (method) => {
    if (disabled) return;
    onChange?.(method);
  };

  return (
    <div
      className={`space-y-2 ${className}`}
      data-testid="payment-method-chooser"
      role="radiogroup"
      aria-label="Choose payment method"
    >
      <div className="text-sm font-medium text-slate-800">How would you like to pay?</div>

      {/* ACH option (hidden for anonymous flows) */}
      {achAvailable && (
        <button
          type="button"
          onClick={() => pick('us_bank_account')}
          disabled={disabled}
          role="radio"
          aria-checked={value === 'us_bank_account'}
          data-testid="pm-choice-ach"
          className={`w-full text-left flex items-center justify-between gap-3 rounded-lg border p-3 transition-all ${
            value === 'us_bank_account'
              ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
              : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              value === 'us_bank_account' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700'
            }`}>
              <Building2 className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">
                Bank transfer (ACH)
              </div>
              <div className="text-xs text-emerald-700 font-medium">
                Save {surchargePercent}% — no processing fee
              </div>
              {savedAchLast4 && (
                <div
                  className="text-[11px] text-emerald-900/80 font-medium mt-0.5 flex items-center gap-1"
                  data-testid="pm-choice-ach-saved"
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-emerald-700" />
                  Pays from {savedAchBank || 'bank'} ••{savedAchLast4}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-slate-900">{fmt(achTotal)}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">total</div>
          </div>
        </button>
      )}

      {/* Card option */}
      <button
        type="button"
        onClick={() => pick('card')}
        disabled={disabled}
        role="radio"
        aria-checked={value === 'card'}
        data-testid="pm-choice-card"
        className={`w-full text-left flex items-center justify-between gap-3 rounded-lg border p-3 transition-all ${
          value === 'card'
            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
            : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            value === 'card' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'
          }`}>
            <CreditCard className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900">Credit / debit card</div>
            <div className="text-xs text-slate-500">
              Includes {surchargePercent}% processing fee (+{fmt(surcharge)})
            </div>
            {savedCardLast4 && (
              <div
                className="text-[11px] text-blue-900/80 font-medium mt-0.5 flex items-center gap-1"
                data-testid="pm-choice-card-saved"
              >
                <span className="inline-block w-1 h-1 rounded-full bg-blue-700" />
                Pays from {savedCardBrand ? savedCardBrand.toUpperCase() : 'card'} ••{savedCardLast4}
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-slate-900">{fmt(cardTotal)}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">total</div>
        </div>
      </button>

      {!value && achAvailable && !disabled && (
        <p className="text-xs text-slate-500 pt-1">
          Pick one to continue to checkout.
        </p>
      )}
    </div>
  );
}

/** Loading skeleton for use while the parent component is fetching pricing. */
export function PaymentMethodChooserSkeleton() {
  return (
    <div className="space-y-2" data-testid="payment-method-chooser-skeleton">
      <div className="text-sm font-medium text-slate-400 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading payment options…
      </div>
      <div className="h-16 rounded-lg bg-slate-100 animate-pulse" />
      <div className="h-16 rounded-lg bg-slate-100 animate-pulse" />
    </div>
  );
}
